import React, { useMemo } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { FormGroup, Input, Label, UncontrolledTooltip } from 'reactstrap';
import { uniqueId } from 'lodash';

import { modelOf } from '../../../prop-types';
import FormField from '../../../models/FormField';
import Field from '../Field';
import FieldError from '../FieldError';
import Icon from '../../common/Icon';
import FloatingLabelWrapper from '../FloatingLabelWrapper';
import { hasFloatingValue } from '../floatingLabelUtils';

const BASE_CLASS = 'SelectField';

const SelectField = ({
  label,
  fieldName,
  field,
  options,
  required,
  tooltip,
  formName,
  floatingLabel,
  customError,
  'aria-label': ariaLabel,
  ...rest
}) => {
  const fieldId = useMemo(
    () => uniqueId(`${formName}__${fieldName}--`),
    [formName, fieldName]
  );

  const optionElements = useMemo(
    () =>
      options.map((option) => (
        <option
          key={uniqueId(option.value)}
          value={option.value}
          disabled={option.disabled}
        >
          {option.label}
        </option>
      )),
    [options]
  );

  const ariaLabelString = useMemo(() => {
    if (!label && ariaLabel) {
      return typeof ariaLabel === 'string' ? ariaLabel : undefined;
    }
    return undefined;
  }, [label, ariaLabel]);

  const useFloating = floatingLabel && label != null;
  const controlledValue = rest.value;
  const hasValue =
    hasFloatingValue(field) ||
    (controlledValue !== undefined &&
      controlledValue !== null &&
      String(controlledValue).trim() !== '');

  const legendContent = label || ariaLabel || fieldName;

  if (useFloating) {
    return (
      <FormGroup
        className={`${BASE_CLASS} ${BASE_CLASS}--floating${
          hasValue ? ` ${BASE_CLASS}--has-value` : ''
        }`}
        tag="fieldset"
      >
        <legend className="sr-only">{legendContent}</legend>
        <FloatingLabelWrapper
          baseClass={BASE_CLASS}
          id={fieldId}
          label={label}
          required={required}
          tooltip={tooltip}
        >
          <Field
            component={Input}
            field={field}
            type="select"
            name={fieldName}
            id={fieldId}
            aria-label={ariaLabelString}
            {...rest}
          >
            {optionElements}
          </Field>
        </FloatingLabelWrapper>
        <FieldError field={field} customError={customError} />
      </FormGroup>
    );
  }

  const tooltipElement = tooltip ? (
    <>
      <Icon name="info" className={`tooltip-${fieldId}`} />
      <UncontrolledTooltip target={`.tooltip-${fieldId}`}>
        {tooltip}
      </UncontrolledTooltip>
    </>
  ) : null;

  return (
    <FormGroup className={BASE_CLASS} tag="fieldset">
      <legend className="sr-only">{legendContent}</legend>
      {label ? (
        <Label className={`${BASE_CLASS}__label`} htmlFor={fieldId}>
          {label}
          {required && (
            <span className={`${BASE_CLASS}__required-mark`}>*</span>
          )}
          {tooltipElement}
        </Label>
      ) : (
        <Label className="sr-only" htmlFor={fieldId}>
          {ariaLabel || fieldName}
        </Label>
      )}
      <Field
        component={Input}
        field={field}
        type="select"
        name={fieldName}
        id={fieldId}
        aria-label={ariaLabelString}
        {...rest}
      >
        {optionElements}
      </Field>
      <FieldError field={field} customError={customError} />
    </FormGroup>
  );
};

SelectField.propTypes = {
  field: modelOf(FormField).isRequired,
  label: PropTypes.node,
  formName: PropTypes.string.isRequired,
  fieldName: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      disabled: PropTypes.bool,
      value: PropTypes.string,
      label: PropTypes.node,
    })
  ).isRequired,
  required: PropTypes.bool,
  tooltip: PropTypes.string,
  floatingLabel: PropTypes.bool,
  customError: PropTypes.string,
  'aria-label': PropTypes.node, // Accept both string and React elements (e.g., FormattedMessage)
};

SelectField.defaultProps = {
  floatingLabel: false,
};

// Set displayName for better debugging and Enzyme test compatibility
SelectField.displayName = 'SelectField';

export default observer(SelectField);
